/**
 * Client-brand switcher store for the Social Media SaaS app.
 *
 * Users (agency staff) manage multiple client brands from one login.
 * The selected brand scopes all list views (posts, accounts, calendar, inbox)
 * to that brand via the `client_brand_id` query param.
 *
 * `selectedBrandId === null` means "All clients" — show unfiltered data.
 *
 * Persisted to localStorage so a page refresh keeps the selection, along with
 * the most recently used clients (switcher "Recent" group).
 *
 * @module lib/client-brand
 */

import { createAppStore } from "@plerity/dashboard-shared/stores";
import { persist } from "zustand/middleware";

/** How many recently used clients the switcher remembers. */
export const MAX_RECENT_BRANDS = 3;

interface ClientBrandState {
  /** Currently selected client brand UUID — null means "All clients" */
  selectedBrandId: string | null;
  /** Most recently selected brand ids, newest first (never includes null). */
  recentBrandIds: string[];
  setSelectedBrandId: (id: string | null) => void;
  reset: () => void;
}

export const useClientBrandStore = createAppStore<ClientBrandState>(
  "social-client-brand",
  persist(
    (set) => ({
      selectedBrandId: null,
      recentBrandIds: [],
      setSelectedBrandId: (id) =>
        set((state) => ({
          selectedBrandId: id,
          recentBrandIds: id
            ? [id, ...(state.recentBrandIds ?? []).filter((r) => r !== id)].slice(0, MAX_RECENT_BRANDS)
            : (state.recentBrandIds ?? []),
        })),
      reset: () => set({ selectedBrandId: null, recentBrandIds: [] }),
    }),
    {
      name: "social-selected-brand",
      partialize: (state) => ({ selectedBrandId: state.selectedBrandId, recentBrandIds: state.recentBrandIds }),
    }
  )
);
